import type { ApiEstimate, ApiEstimateImage } from "@/lib/api/types"
import { readIntakeMeta } from "@/lib/api/public-consumer"
import type { DamageZone } from "../types"
import { buildShotPlan, ZONE_ORDER } from "./shot-plan"
import { sortIntoSlots } from "./import/sort-imports"
import {
  EMPTY_CAPTURE,
  type CaptureState,
  type PlannedShot,
  type ShotId,
  type ShotRecord,
} from "./types"

// Server → capture state, for a phone that continues a lead (QR / SMS link)
// or the desktop reading the phone's progress. Photos the walk took carry
// their shot id (image.metadata.shot); anything else (desktop drop, an older
// lead) is sorted into the open slots. Pure; unit-tested.

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

// The walk step a photo was taken for (any string, including `extra:*`), or
// null for photos that never went through the walk (desktop drop, old leads).
export function shotIdOf(image: Pick<ApiEstimateImage, "metadata">): string | null {
  const shot = isRecord(image.metadata) ? image.metadata.shot : null
  const id = isRecord(shot) ? shot.id : null
  return typeof id === "string" && id.length > 0 ? id : null
}

export function zonesFrom(raw: readonly string[] | null): DamageZone[] {
  return (raw ?? []).filter((z): z is DamageZone => (ZONE_ORDER as readonly string[]).includes(z))
}

export function recordFromImage(image: ApiEstimateImage, key: string, slot: PlannedShot | null): ShotRecord {
  const qualified = isRecord(image.metadata) ? image.metadata.qualified !== false : true
  return {
    key,
    status: "attached",
    previewUrl: image.src,
    uploadedUrl: image.src,
    plannedSide: slot?.side ?? image.side,
    detectedSide: image.side,
    qualityScore: image.quality ?? 0,
    qualified,
    qualityIssues: [],
    description: image.description ?? null,
    // Already on the lead and seen by the consumer once — don't re-nag.
    verdict: "good",
    phone: null,
    accepted: true,
    source: "manual",
    retakes: 0,
    takenAt: Date.parse(image.createdAt) || 0,
  }
}

export interface HydrateOptions {
  // The shop asks for the vehicle (decides whether the walk has a sticker).
  shopWantsVehicle: boolean
}

export function captureFromEstimate(estimate: ApiEstimate, options: HydrateOptions): CaptureState {
  const meta = readIntakeMeta(estimate)
  const answered = meta?.damageZones ?? null
  const zones = zonesFrom(answered)
  const unsure = answered !== null && zones.length === 0
  const zonesConfirmed = answered !== null

  const active = (estimate.images ?? [])
    .filter((img) => img.isActive !== false)
    .sort((a, b) => (Date.parse(a.createdAt) || 0) - (Date.parse(b.createdAt) || 0))

  const plan = buildShotPlan({
    zones,
    unsure,
    includeSticker:
      options.shopWantsVehicle &&
      (!estimate.vehicle?.vin || active.some((img) => shotIdOf(img) === "sticker")),
  })
  const inPlan = new Map(plan.map((s) => [s.id, s]))

  const shots: Partial<Record<ShotId, ShotRecord>> = {}
  const unplaced: ApiEstimateImage[] = []
  const walkedExtras: ApiEstimateImage[] = []
  for (const img of active) {
    const id = shotIdOf(img)
    const slot = id ? inPlan.get(id as ShotId) : undefined
    // Latest photo for a shot wins (images are sorted oldest → newest).
    if (id && slot) shots[id as ShotId] = recordFromImage(img, id, slot)
    // Taken for a step this walk no longer has (the damage answer changed):
    // keep it as an extra, never re-file it into a different slot.
    else if (id) walkedExtras.push(img)
    else unplaced.push(img)
  }

  const { placed, extras } = sortIntoSlots(
    unplaced.map((img) => ({ key: String(img.id), side: img.side })),
    plan,
    new Set(Object.keys(shots) as ShotId[]),
  )
  const byKey = new Map(unplaced.map((img) => [String(img.id), img]))
  for (const [id, key] of placed) {
    const img = byKey.get(key)
    if (img) shots[id] = recordFromImage(img, id, inPlan.get(id) ?? null)
  }

  return {
    ...EMPTY_CAPTURE,
    zones,
    unsure,
    zonesConfirmed,
    shots,
    extras: [
      ...walkedExtras,
      ...extras.map((key) => byKey.get(key)).filter((img): img is ApiEstimateImage => !!img),
    ].map((img) => recordFromImage(img, shotIdOf(img)?.startsWith("extra:") ? shotIdOf(img)! : `extra:${img.id}`, null)),
    photosDoneAt: meta?.photosDoneAt ? Date.parse(meta.photosDoneAt) || Date.now() : null,
  }
}
